import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Lightbulb, ChevronRight, Loader2, Copy, Download, Edit2, Save, ArrowRight, X, Mic, FileText, Bookmark, Upload, Trash2, ChevronDown, Clipboard, Undo2, Wand2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import nexisLogo from "@/assets/nexis-logo-new.png";
import { useCredits } from "@/hooks/useCredits";
import { CreditCostBadge } from "@/components/CreditGate";

// Extract only voiceover/narration from script (removes scene descriptions, timecodes, visuals)
const extractVoiceover = (script: string): string => {
  const lines = script.split("\n");
  const voiceoverLines: string[] = [];

  const isStageDirection = (trimmed: string) => {
    if (!trimmed) return true;
    // Scene/visual directions
    if (/^\[(scene|visual|text overlay|music|sfx|sound|cut to|b-roll)\b/i.test(trimmed)) return true;
    // Any fully-bracketed production note
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) return true;
    // Timecodes like (0:00-0:05)
    if (/^\(\d+:\d+\s*-\s*\d+:\d+\)$/.test(trimmed)) return true;
    // Section headers that are all caps and end with colon
    if (/^[A-Z\s]+:$/.test(trimmed)) return true;
    return false;
  };

  const matchSpeakerLine = (trimmed: string) => {
    // **Narrator:** Hello
    const md = trimmed.match(/^\*\*([^*]+)\*\*:\s*(.*)$/);
    if (md) return { text: md[2] ?? "", opensBlock: true };

    // NARRATOR (V.O.): Hello
    const narrator = trimmed.match(/^NARRATOR\s*\(V\.?O\.?\):\s*(.*)$/i);
    if (narrator) return { text: narrator[1] ?? "", opensBlock: true };

    // VO: Hello / VOICEOVER: Hello
    const vo = trimmed.match(/^(VO|VOICEOVER):\s*(.*)$/i);
    if (vo) return { text: vo[2] ?? "", opensBlock: true };

    // NARRATION: Hello
    const narration = trimmed.match(/^NARRATION:\s*(.*)$/i);
    if (narration) return { text: narration[1] ?? "", opensBlock: true };

    return null;
  };

  let inVoiceoverBlock = false;

  for (const line of lines) {
    const trimmed = line.trim();

    const speaker = matchSpeakerLine(trimmed);
    if (speaker) {
      inVoiceoverBlock = speaker.opensBlock;
      if (speaker.text.trim()) {
        voiceoverLines.push(speaker.text.trim());
      }
      continue;
    }

    if (isStageDirection(trimmed)) {
      inVoiceoverBlock = false;
      continue;
    }

    // If we previously saw a voiceover speaker label line, keep capturing subsequent plain lines
    if (inVoiceoverBlock) {
      voiceoverLines.push(trimmed);
    }
  }

  const extracted = voiceoverLines.join("\n\n").trim();
  if (extracted) return extracted;

  // Fallback: if the script has no explicit narrator/VO labels, keep non-stage-direction lines
  // so the toggle still removes bracketed visuals/timecodes.
  const stripLabel = (t: string) => {
    // Remove **Speaker**: prefix
    t = t.replace(/^\*\*[^*]+\*\*:\s*/, "");
    // Remove NARRATOR (V.O.): prefix
    t = t.replace(/^NARRATOR\s*\(V\.?O\.?\):\s*/i, "");
    // Remove VO: / VOICEOVER: / NARRATION: prefixes
    t = t.replace(/^(VO|VOICEOVER|NARRATION):\s*/i, "");
    return t.trim();
  };

  const fallback = lines
    .map((l) => l.trim())
    .filter((t) => !isStageDirection(t))
    .filter((t) => !/^scene\s*\d+\b[:\-]?/i.test(t))
    .map((t) => stripLabel(t))
    .filter((t) => t.length > 0)
    .join("\n\n")
    .trim();

  return fallback;
};

interface Idea {
  title: string;
  hook: string;
  emotion: string;
  retentionStrategy: string;
  viralityScore: number;
  summary: string;
}

const PRESET_NICHES = [
  "Tech Reviews", "Finance", "Lifestyle", "Gaming", "Education",
  "Fitness", "Cooking", "Travel", "Fashion", "DIY/Crafts",
  "Comedy", "Motivation", "True Crime", "Science", "History"
];

const PLATFORMS = ["TikTok", "YouTube Shorts", "Instagram Reels", "YouTube Long-form"];
const LENGTHS = ["15 seconds", "30 seconds", "60 seconds", "2-3 minutes", "5-10 minutes", "10+ minutes"];
const TONES = ["Casual", "Professional", "Humorous", "Dramatic", "Educational", "Inspirational", "Mysterious"];

interface StyleProfile {
  tone: string;
  pacing: string;
  sentenceStyle: string;
  hookStyle: string;
  vocabularyLevel: string;
  uniqueCharacteristics: string;
}

interface SavedStyle {
  id: string;
  name: string;
  style_profile_json: StyleProfile;
  style_prompt_text: string;
  created_at: string;
}

export default function NexisCreateScript() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { consume, hasEnough } = useCredits();
  
  const [topicChoice, setTopicChoice] = useState<"ai" | "custom">("ai");
  const [customIdea, setCustomIdea] = useState("");
  const [niche, setNiche] = useState("");
  const [customNiche, setCustomNiche] = useState("");
  const [platform, setPlatform] = useState("");
  const [length, setLength] = useState("");
  const [customLength, setCustomLength] = useState("");
  const [tone, setTone] = useState("");
  const [customTone, setCustomTone] = useState("");
  
  // Style/Tone state
  const [styleOption, setStyleOption] = useState<"default" | "copy" | "saved">("default");
  const [referenceText, setReferenceText] = useState("");
  const [analyzedStyleProfile, setAnalyzedStyleProfile] = useState<StyleProfile | null>(null);
  const [analyzedStylePrompt, setAnalyzedStylePrompt] = useState("");
  const [analyzedStyleName, setAnalyzedStyleName] = useState("");
  const [savedStyles, setSavedStyles] = useState<SavedStyle[]>([]);
  const [selectedSavedStyleId, setSelectedSavedStyleId] = useState<string>("");
  const [styleStrength, setStyleStrength] = useState<"close" | "medium" | "loose">("medium");
  const [analyzingStyle, setAnalyzingStyle] = useState(false);
  const [styleExpanded, setStyleExpanded] = useState(false);
  
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<Idea | null>(null);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  
  const [script, setScript] = useState("");
  const [projectId, setProjectId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState("");
  const [voiceoverOnly, setVoiceoverOnly] = useState(false);
  
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [generatingSimilar, setGeneratingSimilar] = useState(false);
  const [generatingScript, setGeneratingScript] = useState(false);
  const [enhancingPrompt, setEnhancingPrompt] = useState(false);
  
  // AI Script Editor state
  const [aiEditPrompt, setAiEditPrompt] = useState("");
  const [isAiEditing, setIsAiEditing] = useState(false);
  const [previousScript, setPreviousScript] = useState<string | null>(null);

  // Sales Content Mode state
  const [salesMode, setSalesMode] = useState(false);
  const [salesProductName, setSalesProductName] = useState("");
  const [salesProductLink, setSalesProductLink] = useState("");
  const [salesProductType, setSalesProductType] = useState("");
  

  const getSelectedNiche = () => customNiche || niche;
  const getSelectedLength = () => customLength || length;
  const getSelectedTone = () => customTone || tone;

  // Fetch saved styles on mount
  useEffect(() => {
    const fetchSavedStyles = async () => {
      if (!user) return;
      const { data, error } = await supabase
        .from("nexis_saved_styles")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      
      if (!error && data) {
        setSavedStyles(data as unknown as SavedStyle[]);
      }
    };
    fetchSavedStyles();
  }, [user]);

  // Get active style prompt for generation
  const getActiveStylePrompt = (): string | null => {
    if (styleOption === "default") return null;
    if (styleOption === "copy" && analyzedStylePrompt) return analyzedStylePrompt;
    if (styleOption === "saved" && selectedSavedStyleId) {
      const style = savedStyles.find(s => s.id === selectedSavedStyleId);
      return style?.style_prompt_text || null;
    }
    return null;
  };

  const canGenerateIdeas = () => {
    // For custom idea, niche is not required - the idea itself provides context
    if (topicChoice === "custom") {
      return platform && getSelectedLength() && getSelectedTone() && customIdea.trim();
    }
    return getSelectedNiche() && platform && getSelectedLength() && getSelectedTone();
  };

  const handleEnhancePrompt = async () => {
    if (!customIdea.trim()) {
      toast.error("Please enter an idea first");
      return;
    }

    setEnhancingPrompt(true);
    try {
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "enhance-prompt",
          idea: customIdea,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      setCustomIdea(data.result);
      toast.success("Idea enhanced with more detail!");
    } catch (error: any) {
      console.error("Error enhancing prompt:", error);
      toast.error(error.message || "Failed to enhance prompt");
    } finally {
      setEnhancingPrompt(false);
    }
  };

  const handleGenerateIdeas = async () => {
    if (!canGenerateIdeas()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setGeneratingIdeas(true);
    try {
      const credited = await consume("generate-script", "Generate ideas");
      if (!credited) { setGeneratingIdeas(false); return; }
      const stylePrompt = getActiveStylePrompt();
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-ideas",
          niche: getSelectedNiche(),
          platform,
          length: getSelectedLength(),
          tone: getSelectedTone(),
          customIdea: topicChoice === "custom" ? customIdea : null,
          stylePrompt,
          styleStrength: stylePrompt ? styleStrength : undefined,
          salesMode,
          salesProductName: salesMode ? salesProductName : undefined,
          salesProductLink: salesMode ? salesProductLink : undefined,
          salesProductType: salesMode ? salesProductType : undefined,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      setIdeas(data.result);
      toast.success("Ideas generated!");
    } catch (error: any) {
      console.error("Error generating ideas:", error);
      toast.error(error.message || "Failed to generate ideas");
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const handleGenerateSimilar = async () => {
    if (!selectedIdea) return;
    
    setGeneratingSimilar(true);
    try {
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "generate-similar",
          idea: selectedIdea,
          platform,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      setIdeas(data.result);
      setShowIdeaModal(false);
      toast.success("Similar ideas generated!");
    } catch (error: any) {
      console.error("Error generating similar ideas:", error);
      toast.error(error.message || "Failed to generate similar ideas");
    } finally {
      setGeneratingSimilar(false);
    }
  };

  const handleGenerateScript = async () => {
    if (!selectedIdea) return;
    
    setGeneratingScript(true);
    setShowIdeaModal(false);
    
    const credited = await consume("generate-script", "Generate script");
    if (!credited) { setGeneratingScript(false); return; }

    // Start saving project immediately (create with placeholder, update later)
    const savePromise = createProjectAndGetId(selectedIdea.title);
    const stylePrompt = getActiveStylePrompt();
    
    try {
      const [scriptResult, savedProjectId] = await Promise.all([
        supabase.functions.invoke("nexis-generate", {
          body: {
            type: "generate-script",
            idea: selectedIdea,
            platform,
            length: getSelectedLength(),
            stylePrompt,
            styleStrength: stylePrompt ? styleStrength : undefined,
            salesMode,
            salesProductName: salesMode ? salesProductName : undefined,
            salesProductLink: salesMode ? salesProductLink : undefined,
            salesProductType: salesMode ? salesProductType : undefined,
          },
        }),
        savePromise,
      ]);

      if (scriptResult.error) throw scriptResult.error;
      if (scriptResult.data.error) throw new Error(scriptResult.data.error);
      
      const generatedScript = scriptResult.data.result;
      setScript(generatedScript);
      
      // Set projectId immediately so the button is enabled
      if (savedProjectId) {
        setProjectId(savedProjectId);
        // Update script in background - don't block UI
        (async () => {
          try {
            await supabase
              .from("nexis_projects")
              .update({ script: generatedScript })
              .eq("id", savedProjectId);
          } catch (err) {
            console.error("Error saving script:", err);
          }
        })();
      }
      
      toast.success("Script generated!");
    } catch (error: any) {
      console.error("Error generating script:", error);
      toast.error(error.message || "Failed to generate script");
    } finally {
      setGeneratingScript(false);
    }
  };
  
  const createProjectAndGetId = async (title: string): Promise<string | null> => {
    if (!user) return null;
    
    try {
      const stylePrompt = getActiveStylePrompt();
      const savedStyleId = styleOption === "saved" ? selectedSavedStyleId : null;
      
      const { data, error } = await supabase
        .from("nexis_projects")
        .insert({
          user_id: user.id,
          title,
          script: "", // Will be updated after script generation
          niche: getSelectedNiche(),
          platform,
          tone: getSelectedTone(),
          length: getSelectedLength(),
          idea_data: selectedIdea as any,
          last_step: "script",
          style_used: styleOption !== "default",
          saved_style_id: savedStyleId || null,
          style_strength: stylePrompt ? styleStrength : "medium",
        })
        .select("id")
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error("Error creating project:", error);
      return null;
    }
  };

  // Style analysis handler
  const handleAnalyzeStyle = async () => {
    if (!referenceText.trim()) {
      toast.error("Please paste a reference script or transcript first");
      return;
    }

    setAnalyzingStyle(true);
    try {
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "analyze-style",
          referenceText,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);
      
      const result = data.result;
      setAnalyzedStyleProfile(result.styleProfile);
      setAnalyzedStylePrompt(result.stylePrompt);
      setAnalyzedStyleName(result.styleName);
      
      // Auto-save the style
      if (user) {
        const { data: savedStyle, error: saveError } = await supabase
          .from("nexis_saved_styles")
          .insert({
            user_id: user.id,
            name: result.styleName,
            style_profile_json: result.styleProfile,
            style_prompt_text: result.stylePrompt,
          })
          .select()
          .single();
        
        if (!saveError && savedStyle) {
          setSavedStyles(prev => [savedStyle as unknown as SavedStyle, ...prev]);
        }
      }
      
      toast.success("Style analyzed and saved!");
    } catch (error: any) {
      console.error("Error analyzing style:", error);
      toast.error(error.message || "Failed to analyze style");
    } finally {
      setAnalyzingStyle(false);
    }
  };

  // Clear style handler
  const handleClearStyle = () => {
    setStyleOption("default");
    setReferenceText("");
    setAnalyzedStyleProfile(null);
    setAnalyzedStylePrompt("");
    setAnalyzedStyleName("");
    setSelectedSavedStyleId("");
  };

  // Delete saved style handler
  const handleDeleteSavedStyle = async (styleId: string) => {
    try {
      const { error } = await supabase
        .from("nexis_saved_styles")
        .delete()
        .eq("id", styleId);
      
      if (error) throw error;
      
      setSavedStyles(prev => prev.filter(s => s.id !== styleId));
      if (selectedSavedStyleId === styleId) {
        setSelectedSavedStyleId("");
      }
      toast.success("Style deleted");
    } catch (error) {
      toast.error("Failed to delete style");
    }
  };

  // File upload handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['.txt', '.docx', 'text/plain', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const isValid = validTypes.some(type => file.name.endsWith(type) || file.type === type);
    
    if (!isValid) {
      toast.error("Please upload a .txt or .docx file");
      return;
    }

    try {
      if (file.name.endsWith('.txt') || file.type === 'text/plain') {
        const text = await file.text();
        setReferenceText(text);
        toast.success("File content loaded");
      } else {
        // For .docx, we'll just read as text (basic extraction)
        const text = await file.text();
        setReferenceText(text);
        toast.success("File content loaded (basic text extraction)");
      }
    } catch (error) {
      toast.error("Failed to read file");
    }
  };

  const getDisplayedScript = () => (voiceoverOnly ? extractVoiceover(script) : script);

  const handleCopyScript = () => {
    navigator.clipboard.writeText(getDisplayedScript());
    toast.success("Script copied to clipboard");
  };

  const handleDownloadScript = () => {
    const blob = new Blob([getDisplayedScript()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedIdea?.title || "script"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Script downloaded");
  };

  const handleSaveEdit = async () => {
    setScript(editedScript);
    setIsEditing(false);
    
    if (projectId) {
      try {
        await supabase
          .from("nexis_projects")
          .update({ script: editedScript })
          .eq("id", projectId);
        toast.success("Script updated");
      } catch (error) {
        console.error("Error saving script:", error);
      }
    }
  };

  // AI Script Editor handler
  const handleAiEdit = async () => {
    if (!aiEditPrompt.trim() || !script) return;
    
    setIsAiEditing(true);
    try {
      const credited = await consume("ai-script-edit", "AI script edit");
      if (!credited) { setIsAiEditing(false); return; }
      // Store current script for undo
      setPreviousScript(script);
      
      const { data, error } = await supabase.functions.invoke("nexis-generate", {
        body: {
          type: "edit-script",
          script: script,
          editPrompt: aiEditPrompt.trim()
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const newScript = data.result;
      setScript(newScript);
      
      // Save to database
      if (projectId) {
        await supabase
          .from("nexis_projects")
          .update({ script: newScript })
          .eq("id", projectId);
      }

      setAiEditPrompt("");
      toast.success("Script updated with AI");
    } catch (error) {
      console.error("Error with AI edit:", error);
      toast.error("Failed to apply AI edit");
    } finally {
      setIsAiEditing(false);
    }
  };

  // Undo AI edit handler
  const handleUndoAiEdit = async () => {
    if (!previousScript) return;
    
    setScript(previousScript);
    
    if (projectId) {
      try {
        await supabase
          .from("nexis_projects")
          .update({ script: previousScript })
          .eq("id", projectId);
        toast.success("Reverted to previous version");
      } catch (error) {
        console.error("Error undoing:", error);
      }
    }
    
    setPreviousScript(null);
  };

  const handleGenerateVisualPrompts = () => {
    if (projectId) {
      navigate(`/app/nexis-ai/project/${projectId}/visual-style`);
    } else {
      toast.error("Please wait for the project to save");
    }
  };

  const getViralityColor = (score: number) => {
    if (score >= 80) return "text-green-400";
    if (score >= 60) return "text-yellow-400";
    return "text-orange-400";
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/nexis-ai")} className="h-9 w-9 md:h-10 md:w-10 flex-shrink-0">
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
          </Button>
          <img src={nexisLogo} alt="Nexis AI" className="w-8 h-8 md:w-10 md:h-10 object-contain flex-shrink-0" />
          <h1 className="font-display text-xl md:text-2xl font-bold">Create Script</h1>
        </div>

        {/* Script Generated View */}
        {script ? (
          <div className="space-y-4 md:space-y-6">
            <Card className="p-4 md:p-6 bg-card/50 border-border/50">
              <div className="flex flex-col gap-3 md:gap-4 mb-4">
                <h2 className="font-display text-lg md:text-xl font-semibold line-clamp-2">{selectedIdea?.title}</h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 md:gap-3">
                  {/* Voiceover Only Toggle */}
                  <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20">
                    <Mic className="w-3 h-3 md:w-4 md:h-4 text-primary" />
                    <Switch
                      id="voiceover-only"
                      checked={voiceoverOnly}
                      onCheckedChange={setVoiceoverOnly}
                    />
                    <Label htmlFor="voiceover-only" className="text-xs md:text-sm cursor-pointer whitespace-nowrap">
                      Voiceover Only
                    </Label>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" onClick={handleCopyScript} className="text-xs md:text-sm h-8 md:h-9">
                      <Copy className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                      Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDownloadScript} className="text-xs md:text-sm h-8 md:h-9">
                      <Download className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                      <span className="hidden sm:inline">Download</span>
                      <span className="sm:hidden">DL</span>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setEditedScript(script);
                        setIsEditing(true);
                      }}
                      className="text-xs md:text-sm h-8 md:h-9"
                    >
                      <Edit2 className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              </div>
              
              {isEditing ? (
                <div className="space-y-3 md:space-y-4">
                  <Textarea
                    value={editedScript}
                    onChange={(e) => setEditedScript(e.target.value)}
                    className="min-h-[300px] md:min-h-[400px] font-mono text-xs md:text-sm bg-background/50"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setIsEditing(false)} size="sm" className="text-xs md:text-sm">
                      Cancel
                    </Button>
                    <Button onClick={handleSaveEdit} size="sm" className="text-xs md:text-sm">
                      <Save className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-background/50 rounded-lg p-3 md:p-4 max-h-[400px] md:max-h-[500px] overflow-y-auto">
                  <pre className="whitespace-pre-wrap font-mono text-xs md:text-sm text-foreground/90">
                    {getDisplayedScript()}
                  </pre>
                </div>
              )}
            </Card>

            {/* AI Script Editor */}
            <Card className="p-4 bg-card/50 border-border/50">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="w-5 h-5 text-primary" />
                <h3 className="font-display font-semibold">AI Script Editor</h3>
                {previousScript && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleUndoAiEdit}
                    className="ml-auto text-xs h-7"
                  >
                    <Undo2 className="w-3 h-3 mr-1" />
                    Undo
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Describe what changes you want to make to the script
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder='e.g., "Make the intro more dramatic" or "Shorten the middle section"'
                  value={aiEditPrompt}
                  onChange={(e) => setAiEditPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isAiEditing && aiEditPrompt.trim()) {
                      handleAiEdit();
                    }
                  }}
                  disabled={isAiEditing}
                  className="flex-1"
                />
                <Button
                  onClick={handleAiEdit}
                  disabled={isAiEditing || !aiEditPrompt.trim()}
                  className="gap-2"
                >
                  {isAiEditing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Applying...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Apply
                    </>
                  )}
                </Button>
              </div>
            </Card>

            <Button
              variant="hero"
              size="lg"
              className="w-full gap-2"
              onClick={handleGenerateVisualPrompts}
              disabled={!projectId}
            >
              {!projectId ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Finalizing...
                </>
              ) : (
                <>
                  Generate Visual Prompts
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </Button>
          </div>
        ) : (
          <>
            {/* Input Form */}
            <Card className="p-6 bg-card/50 border-border/50 mb-6">
              <div className="space-y-6">
                {/* Topic Choice */}
                <div className="space-y-3">
                  <Label className="text-base font-semibold">How would you like to start?</Label>
                  <RadioGroup
                    value={topicChoice}
                    onValueChange={(v) => setTopicChoice(v as "ai" | "custom")}
                    className="grid grid-cols-2 gap-4"
                  >
                    <Label
                      htmlFor="ai-topic"
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        topicChoice === "ai" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <RadioGroupItem value="ai" id="ai-topic" />
                      <div>
                        <Sparkles className="w-5 h-5 text-primary mb-1" />
                        <span className="font-medium">Let AI choose a topic</span>
                      </div>
                    </Label>
                    <Label
                      htmlFor="custom-topic"
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        topicChoice === "custom" ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                      }`}
                    >
                      <RadioGroupItem value="custom" id="custom-topic" />
                      <div>
                        <Lightbulb className="w-5 h-5 text-yellow-400 mb-1" />
                        <span className="font-medium">I have my own idea</span>
                      </div>
                    </Label>
                  </RadioGroup>
                </div>

                {topicChoice === "custom" && (
                  <div className="space-y-2">
                    <Label>Your Idea</Label>
                    <p className="text-sm text-muted-foreground">
                      Describe your video idea in as much detail as possible. Include the topic, key points, target audience, and any specific angles you want to cover. The more context you provide, the better the AI can generate relevant ideas. Don't worry about spelling errors - the AI will understand!
                    </p>
                    <Textarea
                      placeholder="Example: I want to make a video about how introverts can succeed in sales. Focus on the unique strengths introverts have like listening skills and thoughtfulness. Target audience is young professionals who feel disadvantaged. Include real examples and actionable tips..."
                      value={customIdea}
                      onChange={(e) => setCustomIdea(e.target.value)}
                      className="min-h-[120px]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleEnhancePrompt}
                      disabled={enhancingPrompt || !customIdea.trim()}
                      className="gap-2"
                    >
                      {enhancingPrompt ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enhancing...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Enhance Prompt
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Style / Tone Section (Optional) */}
                <Collapsible open={styleExpanded} onOpenChange={setStyleExpanded}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
                      <Label className="text-sm md:text-base cursor-pointer flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Style / Tone (Optional)
                        {styleOption !== "default" && <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Active</span>}
                      </Label>
                      <ChevronDown className={`w-4 h-4 transition-transform ${styleExpanded ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-4">
                    <RadioGroup value={styleOption} onValueChange={(v) => setStyleOption(v as "default" | "copy" | "saved")} className="space-y-3">
                      {/* Default Option */}
                      <Label htmlFor="style-default" className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${styleOption === "default" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                        <RadioGroupItem value="default" id="style-default" className="mt-1" />
                        <div>
                          <span className="font-medium block">Use Nexis AI default style</span>
                          <span className="text-sm text-muted-foreground">We'll generate a script in a proven high-retention style.</span>
                        </div>
                      </Label>

                      {/* Copy Style Option */}
                      <Label htmlFor="style-copy" className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${styleOption === "copy" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                        <RadioGroupItem value="copy" id="style-copy" className="mt-1" />
                        <div className="flex-1">
                          <span className="font-medium block">Copy style from a script/transcript</span>
                          <span className="text-sm text-muted-foreground">Paste a reference and we'll match its tone.</span>
                        </div>
                      </Label>

                      {/* Saved Styles Option */}
                      {savedStyles.length > 0 && (
                        <Label htmlFor="style-saved" className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${styleOption === "saved" ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}>
                          <RadioGroupItem value="saved" id="style-saved" className="mt-1" />
                          <div className="flex-1">
                            <span className="font-medium flex items-center gap-2">
                              <Bookmark className="w-4 h-4" />
                              Use a saved style
                            </span>
                            <span className="text-sm text-muted-foreground">{savedStyles.length} saved style{savedStyles.length !== 1 ? 's' : ''}</span>
                          </div>
                        </Label>
                      )}
                    </RadioGroup>

                    {/* Copy Style Input */}
                    {styleOption === "copy" && (
                      <div className="space-y-3 pl-6 border-l-2 border-primary/30">
                        <div className="relative">
                          <Textarea
                            placeholder="Paste your reference transcript or script here..."
                            value={referenceText}
                            onChange={(e) => setReferenceText(e.target.value)}
                            className="min-h-[120px] text-sm pr-20"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="absolute top-2 right-2 text-xs h-7 px-2"
                            onClick={async () => {
                              try {
                                const text = await navigator.clipboard.readText();
                                setReferenceText(text);
                                toast.success("Pasted from clipboard");
                              } catch {
                                toast.error("Unable to access clipboard");
                              }
                            }}
                          >
                            <Clipboard className="w-3 h-3 mr-1" />
                            Paste
                          </Button>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <label className="cursor-pointer">
                            <input type="file" accept=".txt,.docx" className="hidden" onChange={handleFileUpload} />
                            <Button type="button" variant="outline" size="sm" className="gap-2 pointer-events-none">
                              <Upload className="w-4 h-4" />
                              Upload File
                            </Button>
                          </label>
                          <Button type="button" variant="default" size="sm" onClick={handleAnalyzeStyle} disabled={analyzingStyle || !referenceText.trim()} className="gap-2">
                            {analyzingStyle ? <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</> : <><Sparkles className="w-4 h-4" />Analyze Style</>}
                          </Button>
                        </div>

                        {/* Style Profile Preview */}
                        {analyzedStyleProfile && (
                          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm">{analyzedStyleName}</span>
                              <Button type="button" variant="ghost" size="sm" onClick={handleClearStyle}><X className="w-4 h-4" /></Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div><span className="text-muted-foreground">Tone:</span> {analyzedStyleProfile.tone}</div>
                              <div><span className="text-muted-foreground">Pacing:</span> {analyzedStyleProfile.pacing}</div>
                              <div><span className="text-muted-foreground">Sentences:</span> {analyzedStyleProfile.sentenceStyle}</div>
                              <div><span className="text-muted-foreground">Hook:</span> {analyzedStyleProfile.hookStyle}</div>
                              <div><span className="text-muted-foreground">Vocabulary:</span> {analyzedStyleProfile.vocabularyLevel}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Saved Styles Dropdown */}
                    {styleOption === "saved" && savedStyles.length > 0 && (
                      <div className="space-y-2 pl-6 border-l-2 border-primary/30">
                        <Select value={selectedSavedStyleId} onValueChange={setSelectedSavedStyleId}>
                          <SelectTrigger><SelectValue placeholder="Select a saved style" /></SelectTrigger>
                          <SelectContent>
                            {savedStyles.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedSavedStyleId && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => handleDeleteSavedStyle(selectedSavedStyleId)} className="text-destructive gap-1">
                            <Trash2 className="w-3 h-3" />Delete Style
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Style Strength */}
                    {styleOption !== "default" && (analyzedStylePrompt || selectedSavedStyleId) && (
                      <div className="space-y-2 pl-6 border-l-2 border-primary/30">
                        <Label className="text-sm">Style Strength</Label>
                        <RadioGroup value={styleStrength} onValueChange={(v) => setStyleStrength(v as "close" | "medium" | "loose")} className="flex gap-4">
                          <Label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="close" /><span className="text-sm">Close match</span></Label>
                          <Label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="medium" /><span className="text-sm">Medium</span></Label>
                          <Label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="loose" /><span className="text-sm">Loose</span></Label>
                        </RadioGroup>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                {/* Sales Content Mode */}
                <div className="space-y-3">
                  <div
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
                      salesMode ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"
                    )}
                    onClick={() => setSalesMode(!salesMode)}
                  >
                    <ShoppingBag className={cn("w-5 h-5", salesMode ? "text-primary" : "text-muted-foreground")} />
                    <div className="flex-1">
                      <span className="font-medium block text-sm md:text-base">Sales Content Mode</span>
                      <span className="text-xs text-muted-foreground">Generate scripts designed to sell a specific product</span>
                    </div>
                    <Switch checked={salesMode} onCheckedChange={setSalesMode} />
                  </div>

                  {salesMode && (
                    <div className="space-y-3 pl-4 border-l-2 border-primary/30">
                      <div>
                        <Label className="text-sm">Product Name *</Label>
                        <Input
                          placeholder="e.g. The YouTube Monetization Blueprint"
                          value={salesProductName}
                          onChange={(e) => setSalesProductName(e.target.value)}
                          className="text-sm mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Product Type</Label>
                          <Input
                            placeholder="e.g. eBook, Course"
                            value={salesProductType}
                            onChange={(e) => setSalesProductType(e.target.value)}
                            className="text-sm mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Product Link</Label>
                          <Input
                            placeholder="e.g. link in bio"
                            value={salesProductLink}
                            onChange={(e) => setSalesProductLink(e.target.value)}
                            className="text-sm mt-1"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">Scripts will be structured to drive viewers toward purchasing your product.</p>
                    </div>
                  )}
                </div>

                {/* Niche - only show when AI is choosing topic */}
                {topicChoice === "ai" && (
                  <div className="space-y-2">
                    <Label className="text-sm md:text-base">Niche</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                      <Select value={niche} onValueChange={(v) => { setNiche(v); setCustomNiche(""); }}>
                        <SelectTrigger className="text-sm">
                          <SelectValue placeholder="Select a niche" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRESET_NICHES.map((n) => (
                            <SelectItem key={n} value={n}>{n}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Or type custom niche..."
                        value={customNiche}
                        onChange={(e) => { setCustomNiche(e.target.value); setNiche(""); }}
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}

                {/* Platform */}
                <div className="space-y-2 relative z-0">
                  <Label className="text-sm md:text-base">Platform</Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Select platform" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Length */}
                <div className="space-y-2">
                  <Label className="text-sm md:text-base">Video Length</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                    <Select value={length} onValueChange={(v) => { setLength(v); setCustomLength(""); }}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select length" />
                      </SelectTrigger>
                      <SelectContent>
                        {LENGTHS.map((l) => (
                          <SelectItem key={l} value={l}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Custom (in minutes)..."
                      value={customLength}
                      onChange={(e) => { setCustomLength(e.target.value); setLength(""); }}
                      type="number"
                      min="1"
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Tone */}
                <div className="space-y-2">
                  <Label className="text-sm md:text-base">Tone</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                    <Select value={tone} onValueChange={(v) => { setTone(v); setCustomTone(""); }}>
                      <SelectTrigger className="text-sm">
                        <SelectValue placeholder="Select tone" />
                      </SelectTrigger>
                      <SelectContent>
                        {TONES.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Or type custom tone..."
                      value={customTone}
                      onChange={(e) => { setCustomTone(e.target.value); setTone(""); }}
                      className="text-sm"
                    />
                  </div>
                </div>

                <div className="relative z-20 pt-2">
                  <Button
                    variant="hero"
                    size="lg"
                    className="w-full gap-2"
                    onClick={handleGenerateIdeas}
                    disabled={!canGenerateIdeas() || generatingIdeas}
                  >
                    {generatingIdeas ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating Ideas...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate Ideas
                        <span className="text-xs opacity-75 ml-1">(2 credits)</span>
                      </>
                    )}
                  </Button>
                  {!canGenerateIdeas() && !generatingIdeas && (
                    <p className="text-xs text-muted-foreground text-center mt-2">
                      Fill in all fields above to generate ideas
                    </p>
                  )}
                </div>
              </div>
            </Card>

            {/* Ideas Grid */}
            {ideas.length > 0 && (
              <div className="space-y-4">
                <h2 className="font-display text-xl font-semibold">Video Ideas</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {ideas.map((idea, index) => (
                    <Card
                      key={index}
                      className="p-5 bg-card/50 border-border/50 hover:border-primary/50 cursor-pointer transition-all group"
                      onClick={() => {
                        setSelectedIdea(idea);
                        setShowIdeaModal(true);
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-lg group-hover:text-primary transition-colors line-clamp-2">
                          {idea.title}
                        </h3>
                        <span className={`text-sm font-bold ${getViralityColor(idea.viralityScore)}`}>
                          {idea.viralityScore}%
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{idea.hook}</p>
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-1 rounded-full text-xs bg-primary/10 text-primary border border-primary/20">
                          {idea.emotion}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                          {idea.retentionStrategy}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Generating Script Overlay */}
            {generatingScript && (
              <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
                <Card className="p-8 text-center">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
                  <h3 className="text-xl font-semibold mb-2">Generating Script...</h3>
                  <p className="text-muted-foreground">This may take a moment</p>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Idea Detail Modal */}
        <Dialog open={showIdeaModal} onOpenChange={setShowIdeaModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="pr-8">{selectedIdea?.title}</DialogTitle>
            </DialogHeader>
            {selectedIdea && (
              <div className="space-y-4">
                <div>
                  <Label className="text-muted-foreground">Hook</Label>
                  <p className="mt-1">{selectedIdea.hook}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Emotion</Label>
                    <p className="mt-1 capitalize">{selectedIdea.emotion}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Virality Score</Label>
                    <p className={`mt-1 font-bold ${getViralityColor(selectedIdea.viralityScore)}`}>
                      {selectedIdea.viralityScore}%
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Retention Strategy</Label>
                  <p className="mt-1">{selectedIdea.retentionStrategy}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Summary</Label>
                  <p className="mt-1 text-sm">{selectedIdea.summary}</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleGenerateSimilar}
                    disabled={generatingSimilar}
                  >
                    {generatingSimilar ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Generate Similar Ideas
                  </Button>
                  <Button
                    variant="hero"
                    className="flex-1"
                    onClick={handleGenerateScript}
                    disabled={generatingScript}
                  >
                    Generate Full Script
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
